import { Link } from "react-router-dom";
import { MessageSquare, Sparkles, History, Lock, ArrowRight } from "lucide-react";
import { EXAMPLE } from "@/content/landingExample";

/*
  ProductGallery — "Inside the experience"
  Three panels telling the single fictional example in full
  (content/landingExample.js): what was shared, a fuller picture, a week later.
  Static copy only; nothing here calls the API.
*/
export function ProductGallery() {
  const P = EXAMPLE.panels;
  return (
    <section id="full-example" data-testid="product-gallery" className="bg-blush/20 border-b border-champagne scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid md:grid-cols-2 gap-10 items-end">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve mb-3">Inside the experience</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-cocoa leading-tight">
              A second look can <span className="italic brand-shine">change</span> the picture.
            </h2>
            <p className="mt-4 text-cocoa/70 max-w-md leading-relaxed">{P.intro}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <Stat n="41" label="relationship patterns" to="/archetypes" />
            <Stat n="9" label="relationship stages" />
            <Stat n="Essays" label="on every pattern" small to="/essays" />
            <Stat n="Privacy" label="explained" small to="/security" />
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5 items-stretch">
          <Panel icon={MessageSquare} title={P.shared.title} testid="panel-shared">
            <Label>{P.shared.nowLabel}</Label>
            <Quote>{P.shared.now}</Quote>
            <Label className="mt-4">{P.shared.earlierLabel}</Label>
            <Quote muted>{P.shared.earlier}</Quote>
          </Panel>
          <Panel icon={Sparkles} title={P.picture.title} testid="panel-picture" premium>
            <Label>SugarSense</Label>
            <p className="mt-1 text-[14px] text-cocoa/85 leading-relaxed">{P.picture.advice}</p>
            <p className="mt-4 font-heading text-base text-cocoa">{P.picture.sayLabel}</p>
            <div className="mt-1.5 ss-card p-3 bg-mauve/5">
              <p className="text-[13px] text-cocoa/85 leading-relaxed italic">"{P.picture.say}"</p>
            </div>
          </Panel>
          <Panel icon={History} title={P.later.title} testid="panel-later">
            <Label>{P.later.updateLabel}</Label>
            <Quote>{P.later.update}</Quote>
            <Label className="mt-4">SugarSense</Label>
            <p className="mt-1 text-[14px] text-cocoa/85 leading-relaxed">{P.later.advice}</p>
          </Panel>
        </div>

        <p className="mt-8 text-center">
          <Link to="/register" data-testid="example-try-your-own" className="ss-pill ss-pill-ghost h-11 px-6 inline-flex">Try your own situation <ArrowRight size={14} /></Link>
          <span className="block mt-3 text-sm text-cocoa/55">Your first situation is free. Ongoing history, like the week-later update above, comes with <Link to="/pricing" className="underline underline-offset-2 hover:text-cocoa">paid plans</Link>.</span>
        </p>

        <p className="mt-8 text-center text-xs tracking-widest uppercase text-cocoa/45">
          <Lock size={11} className="inline -mt-0.5 mr-1.5" />
          Private by design · <Link to="/security" className="underline underline-offset-2 hover:text-cocoa">How we keep it that way</Link>
        </p>
      </div>
    </section>
  );
}

function Stat({ n, label, small, to }) {
  const inner = (
    <>
      <p className={`font-heading text-cocoa ${small ? "text-lg sm:text-xl" : "text-3xl sm:text-4xl"}`}>{n}</p>
      <p className="mt-1 text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
    </>
  );
  return to ? <Link to={to} className="ss-card p-4 sm:p-5 block hover:shadow-md transition-shadow">{inner}</Link> : <div className="ss-card p-4 sm:p-5">{inner}</div>;
}

function Panel({ icon: Icon, title, children, testid, premium }) {
  return (
    <figure data-testid={testid} className={`${premium ? "ss-card-premium" : "ss-card"} p-5 flex flex-col`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={12} className="text-mauve" />
        <figcaption className="text-[10px] tracking-[0.3em] uppercase text-mauve">{title}</figcaption>
      </div>
      <div className="ss-card p-4 bg-ivory flex-1">{children}</div>
    </figure>
  );
}

function Label({ children, className = "" }) {
  return <p className={`text-[10px] tracking-[0.2em] uppercase text-cocoa/50 ${className}`}>{children}</p>;
}

function Quote({ children, muted }) {
  return <p className={`mt-1 text-[14px] leading-relaxed ${muted ? "text-cocoa/70" : "text-cocoa/85"}`}>"{children}"</p>;
}
